import bigInt from 'big-integer';
import { format, formatISO, subDays } from 'date-fns';
import { ar, es, fr, ka } from 'date-fns/locale';
import en from 'date-fns/locale/en-US';
import { TFunction } from 'i18next';
import * as yup from 'yup';

import { AssetItemProps } from '@/shared/components/molecules/ChooseAsset/ChooseAsset';
import { roles } from '@/shared/constant/roles-and-permissions';
import { IGeneral } from '@/shared/types';

export function generateRandomIntegerInRange(
  min: number,
  max: number,
  vector?: number
) {
  return Math.floor(Math.random() * (max - min + 1) + min) * (vector || 1);
}

export function randomRGB(vector?: number) {
  return `rgb(${generateRandomIntegerInRange(
    0,
    200,
    vector
  )}, ${generateRandomIntegerInRange(
    0,
    200,
    vector
  )}, ${generateRandomIntegerInRange(0, 200, vector)})`;
}

export function getFixedNumber(num?: number | string | null) {
  return typeof num == 'number'
    ? Number.isInteger(num)
      ? num
      : num.toFixed(2)
    : num;
}
export function hexToRgb(hex: string) {
  const bigint = parseInt(hex.substring(1), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return [r / 255, g / 255, b / 255];
}

export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function base36Encode(largeNumberString: string): string {
  // Convert the string to BigInt
  const bigNumber = bigInt(largeNumberString);

  // Convert the BigInt to a base-36 string
  const base36String = bigNumber.toString(36);

  return base36String;
}

export function base36Decode(base36String: string) {
  // Convert the base-36 string to a BigInt str
  return bigInt(base36String, 36).toString();
}

export const generateDates = (days: number) => {
  return Array.from({ length: days }).map((_, index) =>
    formatISO(subDays(new Date(), days - 1 - index), {
      representation: 'date',
    })
  );
};

export const formatNumericValue = (
  value: number,
  options?: {
    isPercentage?: boolean;
    zeroAsDash?: boolean;
    fractionDigits?: number;
  }
): string => {
  if (value === 0) return options?.zeroAsDash ? '-' : '0';

  const numberOptions: Intl.NumberFormatOptions = {
    minimumFractionDigits: Number.isInteger(value)
      ? 0
      : options?.fractionDigits ?? 2,
    maximumFractionDigits: options?.fractionDigits ?? 2,
    useGrouping: true,
  };

  if (!value) return '';
  let formattedNumber = value?.toLocaleString('en-US', numberOptions);

  if (options?.isPercentage) {
    formattedNumber += '%';
  }

  return formattedNumber;
};

export const convertDecimalHoursToTimeString = (hours: number): string => {
  const wholeHours = Math.floor(hours); // Get the integer part representing whole hours
  const minutes = Math.round((hours - wholeHours) * 60); // Convert the decimal part to minutes

  // Format the string with leading zeros if needed
  const formattedHours = wholeHours.toString();
  const formattedMinutes = minutes.toString().padStart(2, '0');

  return `${formattedHours}:${formattedMinutes}`;
};

export const getUniqueOptions = <T>({
  allOptions,
  selectedOptions,
  getValue,
  exclude,
}: {
  allOptions: T[];
  selectedOptions?: T[];
  getValue: (value?: T | null) => string | number | null | undefined;
  exclude?: T | null;
}) => {
  const optionsSet = new Set();
  selectedOptions?.forEach((selected) => {
    if (getValue(selected)) optionsSet.add(getValue(selected));
  });
  return allOptions.filter(
    (option) =>
      (exclude && getValue(exclude) === getValue(option)) ||
      !optionsSet.has(getValue(option))
  );
};

export const getIsDemoFarm = (asset?: AssetItemProps | null) =>
  asset?.id === -1;

export const truncateString = ({
  text,
  length = 10,
}: {
  text: string;
  length?: number;
}) => {
  return text.length <= length ? text : text.substring(0, length) + '..';
};

export const getDecimalPlacesValidation = (message: string, places = 2) => {
  const multiplier = Math.pow(10, places);

  return yup
    .number()
    .test(
      'maxDigitsAfterDecimal',
      message,
      (val) =>
        !val || Math.abs(Math.round(val * multiplier) - val * multiplier) < 1e-6
    );
};

// TODO: send require error message (optional)
export const nameIdPairValidationSchema = (
  t: TFunction<'translation', undefined>
): yup.SchemaOf<IGeneral.NameIdPair> => {
  return yup.object().shape({
    name: yup.string().required(),
    id: yup.number().required(t('this-field-is-required')),
  });
};
// TODO: the MB & KB should be translated
export const getImageSize = (size: number) => {
  const formattedSize =
    size >= 1048576
      ? `${(size / 1048576).toFixed(1)} MB`
      : `${(size / 1024).toFixed(1)} KB`;

  return formattedSize;
};

export const stepsChecker = (obj: object | null) =>
  obj ? Object.values(obj).filter((value) => !!value).length : 0;

export const getRole = (id: number): string | undefined => {
  return Object.keys(roles).find(
    (key) => roles[key as keyof typeof roles] === id
  );
};

export const formatedDate = (
  date: string,
  formatString: string,
  language: string
) => {
  return format(new Date(date), formatString, {
    locale:
      language === 'ar'
        ? ar
        : language === 'es'
        ? es
        : language === 'fr'
        ? fr
        : language === 'ka'
        ? ka
        : en,
  });
};

/**
 * @description Navigate to a URL in place using window.location.href
 * @param url URL to navigate to
 */
export const navigateToUrl = (url: string) => {
  window.location.href = url;
};

/**
 * @description Capitalizes the first letter of a string
 * @param word string to capitalize
 * @returns capitalized string
 */
export const capitalize = (word: string): string =>
  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
